import { useState } from "react";
import { cn } from "@/lib/utils";

interface CountryFlagProps {
  /** ISO 3166-1 alpha-2 country code (case-insensitive). E.g. "CH", "ES", "AR". */
  code?: string | null;
  /** Optional accessible name. Defaults to the country code. */
  alt?: string;
  className?: string;
}

/**
 * Premium country flag rendered as SVG via flagcdn.com (public CDN).
 * Complies with project standard: SVG flags only (no emojis).
 *
 * Default size: w-7 h-5 (28x20px) with rounded corners and soft shadow.
 */
export const CountryFlag = ({ code, alt, className }: CountryFlagProps) => {
  const normalized = (code || "").trim().toLowerCase();
  const [errored, setErrored] = useState(false);

  if (!normalized || errored) {
    return (
      <span
        aria-hidden="true"
        className={cn(
          "inline-flex items-center justify-center w-7 h-5 rounded-[2px] bg-foreground/10 text-[9px] font-semibold text-foreground/60 ring-1 ring-foreground/10",
          className,
        )}
      >
        {normalized ? normalized.toUpperCase() : "•"}
      </span>
    );
  }

  return (
    <img
      src={`https://flagcdn.com/${normalized}.svg`}
      alt={alt || normalized.toUpperCase()}
      loading="lazy"
      decoding="async"
      onError={() => setErrored(true)}
      className={cn(
        "inline-block w-7 h-5 rounded-[2px] object-cover ring-1 ring-black/10 shadow-sm",
        className,
      )}
    />
  );
};

export default CountryFlag;
